/**
 * DragTextQuiz Student Renderer
 * Student-style UI with larger chips, bigger text, and student color palette.
 */

import { useMemo } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText, SubmitButton, QuizSolveDescription, QuizHeader, QuizResultToggle, type QuizResultToggleValue } from '../shared'
import { formatProblem } from '../../../utils/quiz-parser'
import type { DragTextQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { useDragTextQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface DragTextStudentProps {
  quizId: string | number
  quiz: DragTextQuizData & { choices?: { key?: string | null; answer: string }[]; answers?: Array<string | { key?: string | null; answer: string }> }
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 유형별 노출표에 따라 발문/내용/문제 이미지/보기 텍스트를 흐린다. */
  dimChoiceText?: boolean
  blankPlaceholder?: string
}

export function DragTextStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
  blankPlaceholder,
}: DragTextStudentProps) {
  const { t } = useTranslation('quiz')

  const {
    answers,
    currentIndex,
    blankCount,
    problemKey,
    availableOptions,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolveLabel,
    handleBlankClick,
    handleClearBlank,
    handleSelectOption,
    handleSubmit,
  } = useDragTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // 문제 렌더링
  const renderedProblem = useMemo(() => {
    if (answers.length === 0 && blankCount > 0) return []

    const timestamp = problemKey
    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${timestamp}`,
      (index: number) => {
        const value = answers[index] || ''
        const valueLabel = resolveLabel(value)
        const isActive = currentIndex === index && !isSolved && !isViewSolved
        const hasValue = value !== ''
        const isCorrect = isSolved ? solveResult?.corrects?.[index] : null
        const correctAnswer = solveResult?.correctAnswers?.[index] ?? ''
        const correctAnswerLabel = resolveLabel(correctAnswer)
        const showCorrectAnswer = shouldShowCorrectAnswers && correctAnswerLabel !== ''

        const isCompleted = isSolved || isViewSolved

        return (
          <span
            key={`blank-${quizId}-${timestamp}-${index}`}
            onClick={() => handleBlankClick(index)}
            className={cn(
              'min-w-[48px] px-4 relative my-1 text-[24px] font-medium rounded-2xl mx-2 inline-flex items-center h-12 border-2 border-lc-gray-400 [box-shadow:0px_4px_0px_0px_rgba(0,0,0,0.1)] transition-all duration-200',
              hasValue ? 'justify-between' : 'justify-center',
              // Hover (only when not completed)
              !isCompleted && 'hover:text-lc-lightblue-900 hover:border-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_0px_rgba(85,195,255,0.3)]',
              // Active (current index, no value)
              isActive && !hasValue && 'min-w-[96px] bg-lc-lightblue-200 border-lc-lightblue-900 [box-shadow:unset]',
              // Active (current index, has value)
              isActive && hasValue && 'min-w-[96px] bg-lc-lightblue-200 border-lc-lightblue-900 [box-shadow:unset]',
              // Has value, not active, not solved
              !isSolved && !isViewSolved && hasValue && !isActive && 'bg-lc-lightblue-200 not-[&:hover]:border-none [box-shadow:unset]',
              // Correct
              !showCorrectAnswer && isSolved && isCorrect === true && 'bg-lc-mint-200 text-lc-mint-900 border-none pointer-events-none [box-shadow:unset]',
              // Wrong (not showing correct answers)
              !showCorrectAnswer && isSolved && isCorrect === false && 'bg-lc-red-200 text-lc-red-900 border-none pointer-events-none [box-shadow:unset]',
              // Showing correct answers, including an unanswered blank
              showCorrectAnswer && 'bg-lc-mint-200 text-lc-mint-900 border-none pointer-events-none [box-shadow:unset]',
              // Completed (view solved)
              isViewSolved && 'border-none pointer-events-none [box-shadow:unset]',
            )}
          >
            {showCorrectAnswer ? (
              <KatexText text={correctAnswerLabel} />
            ) : hasValue ? (
              <span className="flex gap-1 items-center">
                <KatexText text={valueLabel} />
                {!isSolved && !isViewSolved && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleClearBlank(index)
                    }}
                    className="w-6 h-6 ml-2 bg-lc-gray-400 rounded-full flex items-center justify-center shrink-0"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L11 11M11 1L1 11" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </span>
            ) : (
              <span className="text-lc-gray-400">{blankPlaceholder ?? t('types.dragText.blank')}</span>
            )}
          </span>
        )
      },
      quiz.additionalImages ?? undefined
    )
  }, [answers, currentIndex, isSolved, solveResult, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, blankCount, handleBlankClick, handleClearBlank, problemKey, quiz.additionalImages, resolveLabel, blankPlaceholder, t])

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.dragText.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        variant="student"
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
        dimTitleText={dimChoiceText}
        dimMedia={dimChoiceText}
      />

      {/* 문제 본문 - legacy FillBoxQuiz style */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        data-quiz-problem-text
        className={cn(
          'text-2xl font-medium text-gray-700',
          dimChoiceText && '[&_.formatProblem]:opacity-[0.05] [&_.formatProblem_other]:opacity-[0.05]'
        )}
      >
        {renderedProblem}
      </motion.div>

      {/* 보기 버튼 - legacy FillBoxQuiz choice chips */}
      {availableOptions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="w-full bg-lc-gray-100 p-6 rounded-2xl mt-10"
        >
          <div className="flex flex-row gap-3 flex-wrap">
            {availableOptions.map((option, idx) => {
              const isCurrentlySelected = answers[currentIndex] === option.key
              return (
                <button
                  key={option.key || `option-${idx}`}
                  onClick={() => handleSelectOption(option.key)}
                  disabled={isSolved || isViewSolved}
                  className={cn(
                    'text-[24px] font-medium min-w-[48px] h-12 flex text-gray-700 items-center justify-center bg-white rounded-2xl border-2 border-lc-gray-400 cursor-pointer [box-shadow:0px_4px_0px_#E0E0E0] px-4 py-3 transition-all duration-150',
                    !isSolved && !isViewSolved && 'hover:border-lc-lightblue-900 hover:text-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.30)]',
                    (isSolved || isViewSolved) && 'cursor-default',
                    isCurrentlySelected && 'border-lc-lightblue-900 bg-lc-lightblue-200 [box-shadow:unset]'
                  )}
                >
                  <span data-quiz-choice-text className={cn(dimChoiceText && 'opacity-[0.05]')}>
                    <KatexText text={option.label} />
                  </span>
                </button>
              )
            })}
          </div>
        </motion.div>
      )}

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
